Ext.define('SA.view.MetadataWindow', {
    alias: 'widget.metadata-window',
    extend: 'SA.view.SimpleContainer',

    statics: {
        destroyInstance() {
            this.getInstance().destroy();
        },

        getInstance() {
            return Ext.ComponentQuery.query('metadata-window')[0] ?? null;
        },

        instanceVisible() {
            return this.getInstance()?.isVisible();
        }
    },

    cls: 'sa-metadata-window',
    floating: true,
    minHeight: 200,
    modal: true,
    plugins: 'sa-viewport-constrained-auto-center',
    width: 660,

    items: [
        {
            xtype: 'simple-container',
            itemId: 'headerWrap',

            items: [
                {
                    xtype: 'component',
                    cls: 'sa-metadata-window-header',
                    html: 'Variable Metadata'
                }, {
                    xtype: 'close-button'
                }
            ]
        }, {
            xtype: 'component',
            cls: 'sa-metadata-window-body',
            itemId: 'dataTable',

            tpl: [
                '<table>',
                    '<tpl if="isUserAttribute">',
                        '<tr><td>Name</td><td><strong>{name:htmlEncode}</strong></td></tr>',
                        '<tr><td>Year</td><td>{year}</td></tr>',
                        '<tr><td>Dataset</td><td><span class="sa-metadata-window-path">{dataset}</span></td></tr>',
                        '<tr><td>Import Date</td><td>{importDate:date("M. j, Y")}</td></tr>',
                    '<tpl else>',
                        '<tr><td>Name</td><td><strong>{name:htmlEncode}</strong></td></tr>',
                        '<tpl if="canAuthenticateByKey">',
                            '<tr>',
                                '<td>Attribute ID</td>',
                                '<td class="sa-metadata-window-attr">',
                                    '<span class="sa-metadata-window-attr-id" id="{parentId}-attr-id">{attributeId:htmlEncode}</span>',
                                    '<sa-copy-button target-id="{parentId}-attr-id" success-text="Attribute ID copied to clipboard.">',
                                        '<button class="sa-metadata-window-copy-btn"><svg><use href="#sa-icon-clipboard"></use></svg></button>',
                                    '</sa-copy-button>',
                                '</td>',
                            '</tr>',
                        '</tpl>',
                        '<tr>',
                            '<td>Year</td>',
                            '<td>',
                                '{year}',
                                `<tpl if="datasetSeries === 'USACS'">`,
                                    `<tpl if="year === 2022 || year === 2023">`,
                                        ' (estimate calculated using 2017-2021 data for this variable)',
                                    `<tpl elseif="year === 2028">`,
                                        ' (projection calculated using 2017-2021 data for this variable)',
                                    '</tpl>',
                                '</tpl>',
                            '</td>',
                        '</tr>',
                        '<tr><td>Path</td><td>{pathHtml}</td></tr>',
                        '<tpl if="universe">',
                            '<tr><td>Universe</td><td>{universe:htmlEncode}</td></tr>',
                        '</tpl>',
                        '<tpl if="vendorTableName">',
                            '<tr><td>Table</td><td>{vendorTableName:htmlEncode}</td></tr>',
                        '</tpl>',
                        '<tpl if="lineNumber">',
                            '<tr><td>Line</td><td>{lineNumber:htmlEncode}</td></tr>',
                        '</tpl>',
                        '<tpl if="geographicRestrictions">',
                            '<tr><td>Geographic Restrictions</td><td>{geographicRestrictions:htmlEncode}</td></tr>',
                        '</tpl>',
                        '<tpl if="definition">',
                            '<tr><td>Definition</td><td>{definition}</td></tr>',
                        '</tpl>',
                        '<tpl if="sourceAgency">',
                            '<tr><td>Source Agency</td><td>{sourceAgency:htmlEncode}</td></tr>',
                        '</tpl>',
                        '<tr><td>Vendor</td><td>{vendor:htmlEncode}</td></tr>',
                        '<tr><td>Dataset</td><td>{dataset:htmlEncode}</td></tr>',
                        '<tpl if="categories">',
                            '<tr><td>Categories</td><td>{categories:htmlEncode}</td></tr>',
                        '</tpl>',
                        '<tpl if="facets.length &gt; 0">',
                            '<tr>',
                                '<td>Tagged</td>',
                                '<td><tpl for="facets"><span class="sa-metadata-window-tag">{.:htmlEncode}</span></tpl></td>',
                            '</tr>',
                        '</tpl>',
                        '<tpl if="dataSource">',
                            '<tr><td>Data Source</td><td>{dataSource}</td></tr>',
                        '</tpl>',
                        '<tpl if="dataSheetFiles.length">',
                            '<tr>',
                                '<td>Data Sheets:</td>',
                                '<td>',
                                    '<tpl for="dataSheetFiles">',
                                        `<img src="{[SA.Assets.getPath('metadata/thumb/' + encodeURIComponent(values))]}"`,
                                            ' class="sa-metadata-window-thumb" data-file="{.:htmlEncode}" data-title="{parent.name:htmlEncode}, {parent.year}">',
                                    '</tpl>',
                                '</td>',
                            '</tr>',
                        '</tpl>',
                    '</tpl>',
                '</table>'
            ]
        }, {
            xtype: 'component',
            cls: 'sa-metadata-window-footer'
        }
    ],

    initComponent() {
        this.callParent();

        this.dataTable = this.down('#dataTable');
        this.headerWrap = this.down('#headerWrap');
    },

    getTplData(data) {
        data = Ext.clone(data);

        var isUserAttribute = this.isUserAttribute(),
            facets = [],
            categories = [],
            dataSheetFiles = (data.metadata_images || []),
            facetIds = SA.data.AttributeFacets.facets.map(({id}) => id);

        data.scarborough_market = Ext.Array.from(data.scarborough_market).join('; ');

        SA.data.AttributeFacets.getGroups().forEach(group => {
            group.facets.forEach((facet, index, groupFacets) => {
                var values = Ext.Array.from(data[facet.id]);

                if (values.length === 0 || values.every(value => !value)) {
                    return;
                }

                if (group.name === 'Category') {
                    categories.push(facet.name);
                }
                else if (groupFacets.length > 1) {
                    facets.push(`${group.name} » ${facet.name}`);
                }
                else if (facetIds.includes(facet.id)) {
                    facets.push(...values.map(value => `${facet.name} » ${value}`));
                }
            });
        });

        return {
            attributeId: this.attribute,
            canAuthenticateByKey: SA.User.getCanAuthenticateByKey(),
            categories: categories.join(', '),
            dataset: SA.data.Datasets.getName(data.dataset_series),
            datasetSeries: data.dataset_series,
            dataSheetFiles,
            dataSource: data.dataset_description,
            definition: data.definition,
            facets: facets.sort(),
            geographicRestrictions: data.geographicRestrictions,
            importDate: getImportDate(),
            isUserAttribute,
            lineNumber: data.lineNumber,
            name: data.name,
            parentId: this.id,
            pathHtml: SA.view.VariablePathRenderer.render({cls: 'sa-metadata-window-path', path: data.group}),
            sourceAgency: data.source,
            universe: data.universe,
            vendor: data.vendor,
            vendorTableName: data.vendorTableName,
            year: data.year
        };

        function getImportDate() {
            var getUserDataset = () => SA.data.UserDatasets.get(data.dataset_series),
                getDate = () => new Date(getUserDataset().imported * 1000);

            return isUserAttribute ? getDate() : null;
        }
    },

    initEvents() {
        this.callParent();

        this.el.on('click', this.onThumbClick, this, {delegate: '.sa-metadata-window-thumb'});
        this.el.on('mouseout', this.onThumbMouseOut, this, {delegate: '.sa-metadata-window-thumb'});
        this.el.on('mouseover', this.onThumbMouseOver, this, {delegate: '.sa-metadata-window-thumb'});

        this.el.on('click', this.onPathClick, this, {delegate: '.sa-metadata-window-path'});
        this.el.on('mouseout', this.onPathMouseOut, this, {delegate: '.sa-metadata-window-path'});
        this.el.on('mouseover', this.onPathMouseOver, this, {delegate: '.sa-metadata-window-path'});

        this.headerWrap.el.on('mousedown', this.onHeaderMouseDown, this);
    },

    isUserAttribute() {
        return SA.data.UserAttributes.isUserAttribute(this.attribute);
    },

    loadAttribute() {
        var facets = SA.data.AttributeFacets.all.map(({id}) => id),
            fields = ['name', 'group', 'universe', 'vendorTableName', 'lineNumber', 'geographicRestrictions', 'definition', 'source', 'dataset_description', 'metadata_images', 'censusRelease', ...facets],
            query = SA.data.Query.from({
                limit: 1,
                type: 'attributes',
                where: ['=', 'attribute', this.attribute],
                fields
            });

        query.addSuccess(({results}) => this.setAttributeData(results[0]));

        query.run();
    },

    onBoxReady() {
        this.callParent(arguments);

        if (this.isUserAttribute()) {
            this.setAttributeData(SA.data.UserAttributes.get(this.attribute));

            return;
        }

        this.dataTable.el.mask('Loading Metadata…');

        this.loadAttribute();
    },

    onHeaderMouseDown(ev) {
        if (ev.getTarget('.sa-close')) {
            return;
        }

        var me = this,
            el = this.el,
            doc = Ext.getDoc(),
            body = Ext.getBody(),
            mousedownXy = ev.getXY(),
            winX = el.getX(),
            winY = el.getY(),
            viewportSize = Ext.dom.Element.getViewSize(),
            constrain = Ext.Number.constrain;

        body.addCls('x-unselectable');

        doc.on('mousemove', onMouseMove);
        doc.on('mouseup', onMouseUp);

        function onMouseMove(ev) {
            var mousemoveXy = ev.getXY(),
                dx = mousemoveXy[0] - mousedownXy[0],
                dy = mousemoveXy[1] - mousedownXy[1],
                maxX = Math.max(0, viewportSize.width - el.getWidth()),
                maxY = Math.max(0, viewportSize.height - el.getHeight()),
                left = constrain(winX + dx, 0, maxX) + 'px',
                top = constrain(winY + dy, 0, maxY) + 'px';

            me.dragged = true;

            el.setStyle({left, top});
        }

        function onMouseUp() {
            body.removeCls('x-unselectable');

            doc.un('mousemove', onMouseMove);
            doc.un('mouseup', onMouseUp);
        }
    },

    onPathClick(ev, el) {
        var path = el.innerText,
            isUserAttribute = this.isUserAttribute(),
            eventData = Object.assign({isUserAttribute, path}, this.pathClickData);

        SA.EventBus.fireEvent('metadata-window:click:path', eventData);

        this.destroy();
    },

    onPathMouseOut(ev, el) {
        Ext.fly(el).removeCls('sa-metadata-window-path-over');
    },

    onPathMouseOver(ev, el) {
        Ext.fly(el).addCls('sa-metadata-window-path-over');
    },

    onThumbClick(ev, thumbEl) {
        var title = thumbEl.getAttribute('data-title'),
            imageSrc = SA.Assets.getPath(`metadata/${encodeURIComponent(thumbEl.getAttribute('data-file'))}`);

        Ext.widget({xtype: 'data-sheet-window', title, imageSrc});
    },

    onThumbMouseOut(ev, img) {
        Ext.fly(img).removeCls('sa-metadata-window-thumb-over');
    },

    onThumbMouseOver(ev, img) {
        Ext.fly(img).addCls('sa-metadata-window-thumb-over');
    },

    setAttributeData(data) {
        var {dataset_series: dataset, year, censusRelease} = data,
            tplData = this.getTplData(data),
            viewportHeight = Ext.dom.Element.getViewportHeight(),
            maxHeight = Math.round(0.6 * viewportHeight);

        this.pathClickData = {dataset, year, censusRelease};

        this.dataTable.update(tplData);

        if (this.getHeight() > maxHeight) {
            this.setHeight(maxHeight);
        }

        if (!this.dragged) {
            this.center();
        }
    }
});
